import React, { useState, useEffect } from "react";
import { BiPlus, BiTrash } from "react-icons/bi";

/**
 * 🔧 ConditionsEditor
 * Édition des conditions d’affichage (x-show-if)
 * Commit uniquement au clic sur "Enregistrer"
 */
export default function ConditionsEditor({ field, schema, onSave, activeTabId = null }) {
  const [draftConditions, setDraftConditions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [availableFields, setAvailableFields] = useState([]);

  /* ===============================
   * Initialisation du draft
   * =============================== */
  useEffect(() => {
    if (showModal) {
      setDraftConditions(field?.["x-show-if"] || []);
    }
  }, [showModal, field]);

  /* ===============================
   * Extraction des champs du schéma
   * =============================== */
  /*
  const extractFieldsRecursive = (
    node,
    prefix = "",
    result = [],
    tabLabel = "",
    breadcrumb = []
  ) => {
    if (!node?.properties) return result;

    for (const [key, prop] of Object.entries(node.properties)) {
      const path = prefix ? `${prefix}.${key}` : key;
      const label = prop.title || key;

      const fullLabel =
        breadcrumb.length > 0
          ? `${[tabLabel, ...breadcrumb, label].join(" › ")}`
          : `[${tabLabel}] ${label}`;

      if (prop.type && prop.type !== "object") {
        result.push({
          name: path,
          title: fullLabel,
          tabLabel,
        });
      }

      if (prop.type === "object") {
        extractFieldsRecursive(
          prop,
          path,
          result,
          tabLabel,
          [...breadcrumb, label]
        );
      }
    }

    return result;
  };
*/
  const extractTabsAndFields = (schema) => {
    const result = [];

    const walk = (node, prefix, breadcrumb = []) => {
      let fields = [];

      for (const [key, prop] of Object.entries(node.properties || {})) {
        const path = `${prefix}.${key}`;
        const label = prop.title || key;

        if (prop.type === "object" && prop.properties) {
          fields = fields.concat(
            walk(prop, path, [...breadcrumb, label])
          );
        } else {
          fields.push({
            name: path,
            title:
              breadcrumb.length > 0
                ? `${[...breadcrumb, label].join(" › ")}`
                : label,
          });
        }
      }

      return fields;
    };

    for (const [tabKey, tab] of Object.entries(schema?.properties || {})) {
      result.push({
        tabKey,
        tabTitle: tab.title || tabKey,
        fields: walk(tab, tabKey),
      });
    }

    return result;
  };

  useEffect(() => {
    setAvailableFields(extractTabsAndFields(schema));
  }, [schema]);
  /* ===============================
   * Helpers édition draft
   * =============================== */
  const addCondition = () =>
    setDraftConditions((prev) => [
      ...prev,
      { name: "", operator: "=", value: "" },
    ]);

  const removeCondition = (i) =>
    setDraftConditions((prev) => prev.filter((_, idx) => idx !== i));

  const updateCondition = (i, key, val) =>
    setDraftConditions((prev) => {
      const arr = [...prev];
      arr[i] = { ...arr[i], [key]: val };
      return arr;
    });

  /* ===============================
   * Résolution du schéma d’un champ
   * =============================== */
  const getFieldSchema = (schema, path) => {
    if (!path) return null;

    const parts = path.split(".");
    let node = schema?.properties?.[parts.shift()];

    for (const part of parts) {
      node = node?.properties?.[part];
      if (!node) return null;
    }

    return node;
  };

  /* ===============================
   * Rendu du champ valeur
   * =============================== */
  const renderValueEditor = (cond, i) => {
    const fieldSchema = getFieldSchema(schema, cond.name);

    if (!fieldSchema) {
      return (
        <input
          type="text"
          className="form-control form-control-sm"
          value={cond.value}
          onChange={(e) => updateCondition(i, "value", e.target.value)}
        />
      );
    }

    if (fieldSchema.enum || fieldSchema["x-options"]) {
      const options =
        fieldSchema["x-options"]?.map((o) => ({
          text: o.text || o.value,
          value: o.value,
        })) ||
        fieldSchema.enum.map((v) => ({ text: v, value: v }));

      return (
        <select
          className="form-select form-select-sm"
          value={cond.value}
          onChange={(e) => updateCondition(i, "value", e.target.value)}
        >
          <option value="">-- Choisir une valeur --</option>
          {options.map((opt, idx) => (
            <option key={idx} value={opt.value}>
              {opt.text}
            </option>
          ))}
        </select>
      );
    }

    if (fieldSchema.type === "boolean") {
      return (
        <select
          className="form-select form-select-sm"
          value={String(cond.value)}
          onChange={(e) =>
            updateCondition(i, "value", e.target.value === "true")
          }
        >
          <option value="">--</option>
          <option value="true">Vrai</option>
          <option value="false">Faux</option>
        </select>
      );
    }

    if (fieldSchema.type === "number" || fieldSchema.type === "integer") {
      return (
        <input
          type="number"
          className="form-control form-control-sm"
          value={cond.value}
          onChange={(e) => updateCondition(i, "value", e.target.value)}
        />
      );
    }

    return (
      <input
        type="text"
        className="form-control form-control-sm"
        value={cond.value}
        onChange={(e) => updateCondition(i, "value", e.target.value)}
      />
    );
  };

  /* ===============================
   * Commit
   * =============================== */
  const handleSave = () => {
    const updatedField = {
      ...field,
      "x-show-if": Array.isArray(draftConditions)
        ? draftConditions
        : [],
    };

    onSave(updatedField);
    setShowModal(false);
  };

  /* ===============================
   * Résumé (source parent)
   * =============================== */
  const conditions = field?.["x-show-if"] || [];

  const renderSummary = () => {
    if (!conditions.length) {
      return (
        <p className="text-muted small mb-2">Aucune condition définie</p>
      );
    }
    return (
      <ul className="small mb-2">
        {conditions.map((c, i) => (
          <li key={i}>
            <code>{c.name}</code> {c.operator || "="}{" "}
            {Array.isArray(c.value) ? c.value.join(", ") : String(c.value)}
          </li>
        ))}
      </ul>
    );
  };

  /* ===============================
   * Render
   * =============================== */
  return (
    <div className="mt-3 border-top pt-2">
      <label className="form-label fw-bold mb-2">
        Conditions d’affichage
      </label>

      {renderSummary()}

      <button
        className="btn btn-sm btn-outline-primary"
        onClick={() => setShowModal(true)}
      >
        Gérer les conditions
      </button>

      {showModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <div className="modal-title">
                  Conditions d’affichage
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                />
              </div>

              <div className="modal-body">
                {draftConditions.map((cond, i) => (
                  <div key={i} className="border rounded p-2 mb-3">
                    {/*
                    <label className="form-label small fw-semibold">
                      Champ déclencheur
                    </label>
                    <select
                      className="form-select form-select-sm mb-2"
                      value={cond.name}
                      onChange={(e) =>
                        updateCondition(i, "name", e.target.value)
                      }
                    >
                      <option value="">
                        -- Sélectionner un champ --
                      </option>
                      {availableFields.map((f, idx) => (
                        <option key={idx} value={f.name}>
                          [{f.tabLabel}] {f.title}
                        </option>
                      ))}
                    </select>
*/}
                    {(() => {
                      const pathParts = cond.name ? cond.name.split(".") : [];
                      const selectedTab =
                        pathParts.length > 0
                          ? pathParts[0]
                          : activeTabId || "";
                      const selectedField = cond.name || "";

                      const currentTab = availableFields.find(t => t.tabKey === selectedTab);

                      return (
                        <>
                          <label className="form-label small fw-semibold">
                            Onglet
                          </label>

                          <select
                            className="form-select form-select-sm mb-2"
                            value={selectedTab}
                            onChange={(e) => {
                              const newTab = e.target.value;

                              // On met juste le tab temporairement
                              updateCondition(i, "name", newTab);
                            }}
                          >
                            <option value="">-- Sélectionner un onglet --</option>
                            {availableFields.map((tab) => (
                              <option key={tab.tabKey} value={tab.tabKey}>
                                {tab.tabTitle}
                              </option>
                            ))}
                          </select>

                          {selectedTab && currentTab && (
                            <>
                              <label className="form-label small fw-semibold">
                                Champ
                              </label>

                              <select
                                className="form-select form-select-sm mb-2"
                                value={
                                  selectedField.includes(".")
                                    ? selectedField
                                    : ""
                                }
                                onChange={(e) =>
                                  updateCondition(i, "name", e.target.value)
                                }
                              >
                                <option value="">-- Sélectionner un champ --</option>

                                {currentTab.fields.map((f) => (
                                  <option key={f.name} value={f.name}>
                                    {f.title}
                                  </option>
                                ))}
                              </select>
                            </>
                          )}
                        </>
                      );
                    })()}


                    <div className="d-flex gap-2 align-items-start">
                      <div style={{ width: "25%" }}>
                        <label className="form-label small">
                          Opérateur
                        </label>
                        <select
                          className="form-select form-select-sm"
                          value={cond.operator || "="}
                          onChange={(e) =>
                            updateCondition(
                              i,
                              "operator",
                              e.target.value
                            )
                          }
                        >
                          <option value="=">=</option>
                          <option value="!=">≠</option>
                          <option value=">">&gt;</option>
                          <option value=">=">&gt;=</option>
                          <option value="<">&lt;</option>
                          <option value="<=">&lt;=</option>
                          <option value="in">in</option>
                          <option value="not in">not in</option>
                        </select>
                      </div>

                      <div className="flex-grow-1">
                        <label className="form-label small">
                          Valeur
                        </label>
                        {renderValueEditor(cond, i)}
                      </div>

                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger mt-4"
                        onClick={() => removeCondition(i)}
                      >
                        <BiTrash />
                      </button>
                    </div>
                  </div>
                ))}

                <button
                  className="btn btn-sm btn-outline-primary mt-2"
                  onClick={addCondition}
                >
                  <BiPlus /> Ajouter une condition
                </button>
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Annuler
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                >
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
